<template>
  <div class="kt-date-range-select">
    <button 
      type="button"
      class="date-range-trigger"
      @click="openModal"
    >
      <span class="trigger-label">{{ displayLabel }}</span>
      <i class="fas fa-chevron-down ms-1"></i>
    </button>
    
    <kt-modal
      :visible="modalVisible"
      title="Select Date Range"
      @dismiss="closeModal"
      size="modal-lg"
    >
      <template #body>
        <div class="container-fluid">
          <div class="row mb-3">
            <div class="col-12">
              <label class="form-label">Date Range Preset</label>
              <select 
                class="form-select" 
                v-model="datePreset"
                @change="applyDatePreset"
              >
                <option value="">Custom Range</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this-week">This Week</option>
                <option value="last-week">Last Week</option>
                <option value="this-month">This Month</option>
                <option value="last-month">Last Month</option>
              </select>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <kt-input
                id="start-date"
                type="date"
                label="Start Date"
                v-model="tempStartDate"
                :input-class="'form-control'"
              />
            </div>
            <div class="col-md-6">
              <kt-input
                id="end-date"
                type="date"
                label="End Date"
                v-model="tempEndDate"
                :input-class="'form-control'"
              />
            </div>
          </div>
          <div class="row mt-3" v-if="validationError">
            <div class="col-12">
              <div class="alert alert-danger" role="alert">
                {{ validationError }}
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <button 
          class="btn btn-outline-secondary me-2" 
          @click="clearDates"
        >
          Clear
        </button>
        <button 
          class="btn btn-outline-primary" 
          @click="applyDates"
          :disabled="!!validationError"
        >
          Apply
        </button>
      </template>
    </kt-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import KtModal from './KtModal.vue';
import KtInput from './KtInput.vue';

const props = defineProps({
  label: {
    type: String,
    default: 'Date Range'
  }
});

const emit = defineEmits<{
  change: [value: { startDate: string | null; endDate: string | null }];
}>();

const model = defineModel<{ startDate: string | null; endDate: string | null }>({
  default: () => ({ startDate: null, endDate: null })
});

const modalVisible = ref(false);
const tempStartDate = ref<string>('');
const tempEndDate = ref<string>('');
const datePreset = ref<string>('');

const displayLabel = computed(() => {
  if (model.value.startDate || model.value.endDate) {
    const start = model.value.startDate ? formatDate(model.value.startDate) : '...';
    const end = model.value.endDate ? formatDate(model.value.endDate) : '...';
    return `${start} - ${end}`;
  }
  return props.label;
});

const validationError = computed(() => {
  if (!tempStartDate.value || !tempEndDate.value) {
    return null; // Allow empty dates
  }
  
  const start = new Date(tempStartDate.value);
  const end = new Date(tempEndDate.value);
  
  if (start > end) {
    return 'Start date must be before or equal to end date';
  }
  
  return null;
});

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const openModal = () => {
  // Sync current model values to temp values
  tempStartDate.value = model.value.startDate || '';
  tempEndDate.value = model.value.endDate || '';
  datePreset.value = '';
  modalVisible.value = true;
};

const closeModal = () => {
  modalVisible.value = false;
};

const applyDates = () => {
  if (validationError.value) return;
  
  model.value = {
    startDate: tempStartDate.value || null,
    endDate: tempEndDate.value || null
  };
  emit('change', model.value);
  modalVisible.value = false;
};

const clearDates = () => {
  tempStartDate.value = '';
  tempEndDate.value = '';
  datePreset.value = '';
  model.value = { startDate: null, endDate: null };
  emit('change', model.value);
  modalVisible.value = false;
};

const applyDatePreset = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  let startDate: Date, endDate: Date;
  
  switch (datePreset.value) {
    case 'today':
      startDate = new Date(today);
      endDate = new Date(today);
      break;
      
    case 'yesterday':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 1);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      break;
      
    case 'this-week':
      // Sunday of this week to Saturday of this week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOfWeek);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - dayOfWeek + 6);
      break;
      
    case 'last-week':
      // Sunday to Saturday of last week
      startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOfWeek - 7);
      endDate = new Date(today);
      endDate.setDate(today.getDate() - dayOfWeek - 1);
      break;
      
    case 'this-month':
      // First day of this month to today
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today);
      break;
      
    case 'last-month':
      // First day to last day of last month
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
      
    default:
      // Custom range - don't change dates
      return;
  }
  
  // Format dates as YYYY-MM-DD
  tempStartDate.value = startDate.toISOString().split('T')[0];
  tempEndDate.value = endDate.toISOString().split('T')[0];
};

// Watch for external model changes
watch(() => model.value, (newValue) => {
  tempStartDate.value = newValue.startDate || '';
  tempEndDate.value = newValue.endDate || '';
}, { deep: true });
</script>

<style scoped>
.kt-date-range-select {
  display: inline-block;
}

.date-range-trigger {
  background: transparent;
  border: 1px solid transparent;
  color: #444;
  cursor: pointer;
  text-decoration: none;
  padding: 0.375rem 0.75rem;
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.date-range-trigger:hover {
  color: #0a58ca;
  background-color: #f8f9fa;
  border-color: #dee2e6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.date-range-trigger:active,
.date-range-trigger:focus {
  background-color: #e9ecef;
  border-color: #ced4da;
  outline: none;
}

.date-range-trigger .trigger-label {
  font-weight: 300;
}
</style>
